import { useState } from 'react';
import { supabase } from '../lib/supabase';
import { useI18n } from '../lib/i18n';

// ============================================================
// PRIJAVA — bez samostalne registracije: naloge otvara FinArt Studio (pozivnicom).
// Registracija je isključena i na Supabase-u (Allow new users to sign up = off).
// ============================================================
const TX = {
  sr: {
    noSignup: 'Nalog dobijate od FinArt Studija. Za pristup pišite na',
    resetSent: 'Ako nalog postoji, poslali smo vam e-mail sa linkom za promenu lozinke. Pogledajte i folder Spam.',
    sendLink: 'Pošalji link',
    headline: 'Za paušalce koji ne vole papirologiju.',
    sub: 'Fakture, KPO knjiga, limiti i porezi, uvek pod kontrolom.',
  },
  en: {
    noSignup: 'Your account is created by FinArt Studio. For access, write to',
    resetSent: 'If the account exists, we have sent you an e-mail with a link to change your password. Please also check your Spam folder.',
    sendLink: 'Send link',
    headline: 'For flat-rate entrepreneurs who don’t like paperwork.',
    sub: 'Invoices, KPO book, limits and taxes, always under control.',
  },
  ru: {
    noSignup: 'Учётную запись открывает FinArt Studio. Для доступа напишите на',
    resetSent: 'Если учётная запись существует, мы отправили письмо со ссылкой для смены пароля. Проверьте также папку «Спам».',
    sendLink: 'Отправить ссылку',
    headline: 'Для паушалистов, которые не любят бумажную волокиту.',
    sub: 'Счета, книга КПО, лимиты и налоги — всегда под контролем.',
  },
};
const SUPPORT_EMAIL = 'podrska@finart.rs';

export default function LoginPage() {
  const { t, lang, setLang } = useI18n();
  const tx = TX[lang] || TX.sr;
  const [mode, setMode] = useState('login'); // 'login' | 'forgot'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');
    setLoading(true);

    try {
      if (mode === 'forgot') {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/reset-password`,
        });
        if (error) throw error;
        setInfo(tx.resetSent);
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
      }
    } catch (err) {
      setError(translateAuthError(err.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.langSwitch}>
        <button
          onClick={() => setLang('sr')}
          style={{ ...styles.langBtn, ...(lang === 'sr' ? styles.langBtnActive : {}) }}
        >SR</button>
        <button
          onClick={() => setLang('en')}
          style={{ ...styles.langBtn, ...(lang === 'en' ? styles.langBtnActive : {}) }}
        >EN</button>
        <button
          onClick={() => setLang('ru')}
          style={{ ...styles.langBtn, ...(lang === 'ru' ? styles.langBtnActive : {}) }}
        >RU</button>
      </div>

      <div style={styles.split}>
        {/* LEFT — form */}
        <div style={styles.formSide}>
          <div style={styles.formWrap}>
            <div style={styles.logoRow}>
              <div style={styles.logoBadge}>FA</div>
              <span style={styles.logoText}>FinArt Studio</span>
            </div>

            <h1 style={styles.heading}>
              {mode === 'forgot' ? t('forgotPassword') : t('login')}
            </h1>

            <form onSubmit={handleSubmit} style={styles.form}>
              <label style={styles.label}>{t('email')}</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="ime@email.com"
                style={styles.input}
              />

              {mode !== 'forgot' && (
                <>
                  <label style={styles.label}>{t('password')}</label>
                  <input
                    type="password"
                    required
                    minLength={6}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    autoComplete="current-password"
                    style={styles.input}
                  />
                </>
              )}

              {mode === 'login' && (
                <div style={{ ...styles.rowBetween, justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    onClick={() => { setMode('forgot'); setError(''); setInfo(''); }}
                    style={styles.linkBtn}
                  >{t('forgotPassword')}</button>
                </div>
              )}

              {error && <div style={styles.errorBox}>{error}</div>}
              {info && <div style={styles.infoBox}>{info}</div>}

              <button type="submit" disabled={loading} style={styles.submitBtn}>
                {loading ? '...' : mode === 'forgot' ? tx.sendLink : t('signIn')}
              </button>

              {mode === 'forgot' && (
                <button
                  type="button"
                  onClick={() => { setMode('login'); setError(''); setInfo(''); }}
                  style={styles.backBtn}
                >{t('backToLogin')}</button>
              )}
            </form>

            {mode === 'login' && (
              <p style={styles.bottomText}>
                {tx.noSignup}{' '}
                <a href={`mailto:${SUPPORT_EMAIL}`} style={styles.linkBtnInline}>{SUPPORT_EMAIL}</a>
              </p>
            )}
          </div>
        </div>

        {/* RIGHT — illustration / brand panel */}
        <div style={styles.brandSide}>
          <div style={styles.brandContent}>
            <div style={styles.brandCircle}>FinArt<br />Studio</div>
            <h2 style={styles.brandHeadline}>{tx.headline}</h2>
            <p style={styles.brandSub}>{tx.sub}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function translateAuthError(msg) {
  if (!msg) return 'Došlo je do greške. Pokušajte ponovo.';
  if (msg.includes('Invalid login credentials')) return 'Pogrešan email ili lozinka.';
  if (msg.includes('Email not confirmed')) return 'Nalog još nije potvrđen. Otvorite link iz pozivnice koju ste dobili e-mailom.';
  if (msg.toLowerCase().includes('rate limit')) return 'Previše pokušaja. Sačekajte par minuta i pokušajte ponovo.';
  return msg;
}

const styles = {
  page: { minHeight: '100vh', background: 'var(--paper)', position: 'relative' },
  langSwitch: { position: 'absolute', top: 20, right: 20, zIndex: 5, display: 'flex', gap: 4 },
  langBtn: {
    border: '1px solid var(--line)', background: 'var(--surface)', borderRadius: 8,
    padding: '6px 10px', fontSize: 13, fontWeight: 600, color: 'var(--ink-500)',
  },
  langBtnActive: { background: 'var(--teal-800)', color: '#fff', borderColor: 'var(--teal-800)' },
  split: { display: 'flex', minHeight: '100vh', flexWrap: 'wrap' },
  formSide: {
    flex: '1 1 420px', display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '48px 24px',
  },
  formWrap: { width: '100%', maxWidth: 380 },
  logoRow: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 32 },
  logoBadge: {
    width: 40, height: 40, borderRadius: 8, background: 'var(--teal-800)',
    color: 'var(--lavender-300)', display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 14,
  },
  logoText: { fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 19, color: 'var(--teal-800)' },
  heading: { fontSize: 28, marginBottom: 20 },
  tabs: { display: 'flex', gap: 4, background: 'var(--paper)', border: '1px solid var(--line)', borderRadius: 8, padding: 4, marginBottom: 24 },
  tab: {
    flex: 1, border: 'none', background: 'transparent', padding: '10px 0', borderRadius: 9,
    fontWeight: 600, fontSize: 14, color: 'var(--teal-700)',
  },
  tabActive: { background: 'var(--surface)', boxShadow: 'var(--shadow-sm)', color: 'var(--teal-800)' },
  form: { display: 'flex', flexDirection: 'column', gap: 8 },
  label: { fontSize: 13, fontWeight: 600, color: 'var(--ink-700)', marginTop: 8 },
  input: {
    border: '1.5px solid var(--line)', borderRadius: 10, padding: '12px 14px',
    fontSize: 15, color: 'var(--ink-900)', background: 'var(--surface)',
  },
  rowBetween: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4, flexWrap: 'wrap', gap: 8 },
  checkboxRow: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--ink-700)' },
  linkBtn: { border: 'none', background: 'none', color: 'var(--teal-700)', fontSize: 13, fontWeight: 600, padding: 0 },
  linkBtnInline: { border: 'none', background: 'none', color: 'var(--lavender-700)', fontWeight: 700, fontSize: 14, padding: 0 },
  errorBox: { background: 'var(--red-100)', color: 'var(--red-600)', borderRadius: 10, padding: '10px 12px', fontSize: 13, marginTop: 8 },
  infoBox: { background: 'var(--forest-100)', color: 'var(--forest-600)', borderRadius: 10, padding: '10px 12px', fontSize: 13, marginTop: 8 },
  submitBtn: {
    marginTop: 16, background: 'var(--teal-800)', color: '#fff', border: 'none',
    borderRadius: 8, padding: '14px 0', fontWeight: 700, fontSize: 15,
  },
  backBtn: { marginTop: 4, border: 'none', background: 'none', color: 'var(--ink-500)', fontSize: 13, padding: '8px 0' },
  bottomText: { textAlign: 'center', marginTop: 24, fontSize: 14, color: 'var(--ink-500)' },
  brandSide: {
    flex: '1 1 420px', background: 'var(--teal-800)', display: 'flex',
    alignItems: 'center', justifyContent: 'center', padding: 48, minHeight: 320,
  },
  brandContent: { maxWidth: 420, textAlign: 'center' },
  brandCircle: {
    width: 130, height: 130, borderRadius: 10, background: 'var(--teal-700)',
    border: '1px solid rgba(214,196,240,0.25)', color: 'var(--lavender-300)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column',
    margin: '0 auto 32px', fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 17, lineHeight: 1.3,
  },
  brandHeadline: { color: '#fff', fontSize: 26, lineHeight: 1.35, marginBottom: 16 },
  brandSub: { color: 'var(--lavender-300)', fontSize: 15, lineHeight: 1.6 },
};
